import {_} from '../libs.js';

let DEFAULT_NUMBER_GROUP_DELIMITER = ',',
    DEFAULT_NUMBER_DECIMALS_DELIMITER = '.',
    util,
    idsCounter = 0,
    startTimesById = {},
    listeners = {},
    /**
     * Throws an error;
     * To be called from within one of the static assert* methods.
     * @param assertionArgs (Arguments) the original arguments from the assert*-call
     * @param [optionalMessageOffset] (Number) optional offset of the actual error message
     *                                         within the assertionArgs (default: 1)
     */
    throwError = function(assertionArgs, optionalMessageOffset) {
        let messageOffset = optionalMessageOffset || 1,
            messageAndArgumentsArray = Array.prototype.slice.call(assertionArgs, messageOffset),
            emptySafeMessageAndArgsArray = (messageAndArgumentsArray.length) ?
                messageAndArgumentsArray : ['Assertion failed'],
            errorMessage = util.formatString.apply(null, emptySafeMessageAndArgsArray);

        /*eslint no-console:0 */
        console.error(errorMessage);
        throw errorMessage;
    },
    /**
     * Origin: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Math/round
     * Decimal adjustment of a number.
     *
     * @param {string} type - the type of adjustment
     * @param {number} num - the number
     * @param {number} expon - the exponent (the 10 logarithm of the adjustment base), e.g. -2 for 2 digits precision
     * @returns (Number) The adjusted value.
     */
    decimalAdjust = function(type, num, expon) {
        // If the exp is undefined or zero...
        if (typeof expon === 'undefined' || +expon === 0) {
            return Math[type](num);
        }
        let value = +num,
            exp = +expon;
        // If the value is not a number or the exp is not an integer...
        if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
            return NaN;
        }
        // Shift
        value = value.toString().split('e');
        value = Math[type](+(value[0] + 'e' + (value[1] ? (+value[1] - exp) : -exp)));
        // Shift back
        value = value.toString().split('e');
        return +(value[0] + 'e' + (value[1] ? (+value[1] + exp) : exp));
    };


util = {

    TIMEZONE_LONDON: 'Europe/London',

    /** @static */
    NOP: function(){},

    /** @static */
    nextId: function() {
        return ++idsCounter;
    },

    /**
     * Returns a given String with placeholders replaced.
     * Contained placeholders '{}' will be replaced with additional parameters in the respective order.
     * @param s (mixed) what to print
     * @params (mixed*) (optional) any number of values replacing the placeholders in s
     * @static
     */
    formatString: function(s) {
        let out = '' + s;
        for (let i = 1, len = arguments.length; i < len; ++i) {
            out = out.replace('{}', arguments[i]);
        }
        return out;
    },

    /**
     * Formats a given number.
     * Inspired by: http://stackoverflow.com/questions/149055/how-can-i-format-numbers-as-money-in-javascript
     *
     * @param numberToFormat (number) the number to format
     * @param [decimals] (number) length of decimal part (default: 2)
     * @param [groupDelimiter] (String) sections delimiter (default: {@link DEFAULT_NUMBER_GROUP_DELIMITER})
     * @param [commaOrPoint] (String) decimal delimiter (default: {@link DEFAULT_NUMBER_DECIMALS_DELIMITER})
     */
    formatNumber: function(numberToFormat, decimals, groupDelimiter, commaOrPoint) {
        let groupSize = 3,
            _decimals = (typeof decimals === 'number') ? decimals : 2,
            re = '\\d(?=(\\d{' + (groupSize || 3) + '})+' + (_decimals > 0 ? '\\D' : '$') + ')',
            decimalsDelimiter = commaOrPoint || DEFAULT_NUMBER_DECIMALS_DELIMITER,
            effectiveGroupDelimiter = groupDelimiter || DEFAULT_NUMBER_GROUP_DELIMITER,
            num = this.round10(numberToFormat, _decimals).toFixed(_decimals);

        return num.replace('.', decimalsDelimiter).replace(new RegExp(re, 'g'), '$&' + (effectiveGroupDelimiter));
    },

    /**
     * @param num (Number)
     * @param [groupDelimiter] (String) default is {@link DEFAULT_NUMBER_GROUP_DELIMITER}
     * @param [commaOrPoint] (String) default is {@link DEFAULT_NUMBER_DECIMALS_DELIMITER}
     * @returns String
     */
    formatMoney: function(num, groupDelimiter, commaOrPoint) {
        return this.formatNumber(num, 2, groupDelimiter, commaOrPoint);
    },

    /** @static */
    assert: function(expr) {
        expr || throwError(arguments);
    },
    /** @static */
    assertDefined: function(expr) {
        (typeof expr !== 'undefined') || throwError(arguments);
    },
    /** @static */
    assertBoolean: function(expr) {
        (typeof expr === 'boolean') || throwError(arguments);
    },
    /** @static */
    assertBooleanOrUndefined: function(expr) {
        (typeof expr === 'boolean' || typeof expr === 'undefined') || throwError(arguments);
    },
    /** @static */
    assertString: function(expr) {
        (typeof expr === 'string') || throwError(arguments);
    },
    /** @static */
    assertNonEmptyString: function(expr) {
        (typeof (!!expr && expr) === 'string') || throwError(arguments);
    },
    /** @static */
    assertStringOrEmpty: function(expr) {
        (!expr || typeof expr === 'string') || throwError(arguments);
    },
    /** @static */
    assertNumber: function(expr) {
        (typeof expr === 'number') || throwError(arguments);
    },
    /** @static */
    assertNumberInRange: function(expr, min, max) {
        (typeof expr === 'number' && expr >= min && expr <= max) || throwError(arguments, 3);
    },
    /** @static */
    assertNumberInRangeOrEmpty: function(expr, min, max) {
        (!expr || (typeof expr === 'number' && expr >= min && expr <= max)) || throwError(arguments, 3);
    },
    /** @static */
    assertFunction: function(expr) {
        (typeof expr === 'function') || throwError(arguments);
    },
    /** @static */
    assertFunctionOrEmpty: function(expr) {
        (!expr || (typeof expr === 'function')) || throwError(arguments);
    },
    /** @static */
    assertObject: function(expr) {
        (expr && typeof expr === 'object') || throwError(arguments);
    },
    /** @static */
    assertObjectOrEmpty: function(expr) {
        (!expr || (typeof expr === 'object')) || throwError(arguments);
    },
    /** @static */
    assertArray: function(expr) {
        (expr && expr instanceof Array) || throwError(arguments);
    },
    /** @static */
    assertArrayOrEmpty: function(expr) {
        (!expr || expr instanceof Array) || throwError(arguments);
    },
    assertElement: function(expr) {
        (expr && _.isElement(expr)) || throwError(arguments);
    },
    assertObjectHasProperties: function(obj, propertyNames) {
        this.assertObject(obj, arguments[2] || 'Given obj parameter is not an object');
        (propertyNames||[]).forEach(function(propName) {
            if (typeof obj[propName] === 'undefined') {
                console.warn(arguments[2] || 'Given object is invalid');
                throwError([obj, 'Missing property: "{}"']);
            }
        });
    },

    /**
     * Pads the given object with leading zeros until its string representation is at least `digits` long.
     * @param {number|string} numOrStr
     * @param {number} digits - intended total digits incl leading zeroes
     * @param {string} [paddChar=0]
     * @returns {string}
     */
    paddLeft: function(numOrStr, digits, paddChar) {
        let s = '' + numOrStr;
        for (let i=digits-s.length, _paddChar = paddChar || '0';
             i>0; i--) {
            s = _paddChar + s;
        }
        return s;
    },

    /**
     * @static
     * Copies all properties of source objects to a given target object.
     * @param target (Object)
     * @param sources (*Object) one or more objects whose properties are to be copied to the target
     * @returns (Object) the target
     */
    extend: function(target, /*...*/sources) {
        return _.extend.apply(_, arguments);
    },

    /**
     * @static
     * Memorizes the current time under a given id.
     * A subsequent call of {@link #stopTimer} will then return the time difference in millis.
     * @param id (Number) some id
     */
    startTimer: function(id) {
        this.assert(!!id, 'invalid id for util.startTime: ', id);
        startTimesById[''+id] = +new Date();
    },
    /**
     * @static
     * Returns the time in milliseconds that passed between now
     * and the last call of {@link #startTimer} for a given id.
     * @param id (Number) some id
     * @return (Number) time in millis; -1 if timerStart wasn't called for that id before
     */
    stopTimer: function(id) {
        let now = +new Date();
        return now - (startTimesById[''+id] || (now + 1));
    },

    /**
     * @static
     * Adds a listener function to be called whenever {@link #notify()} is called for the given event name.
     * @param eventName (String)
     * @param handler (function) callback with any number of arguments it expects from the notifier
     */
    listen: function(eventName, handler) {
        this.assertString(eventName, 'Invalid event name for util.listen()');
        this.assertFunction(handler, 'Invalid handler function for util.listen()');
        if (!listeners[eventName]) {
            listeners[eventName] = [handler];
        } else if (!_.contains(listeners[eventName], handler)) {
            listeners[eventName].push(handler);
        }
    },

    /**
     * @static
     * Removes an event handler (or all) for a given event name.
     * @param eventName (String)
     * @param [handler] (function) if empty, all handlers are removed for the given event name
     */
    unlisten: function(eventName, handler) {
        this.assertString(eventName, 'Invalid event name for util.unlisten()');
        this.assertFunctionOrEmpty(handler, 'Invalid handler function for util.unlisten()');
        if (!handler && listeners[eventName]) {
            delete listeners[eventName];
            // console.log('Removed all event handlers for event "%s"', eventName);
        } else if (handler) {
            _.find(listeners[eventName], function(fn, index) {
                if (fn === handler) {
                    delete listeners[eventName][index];
                    // console.log('Removed handler for event "%s"', eventName);
                    return true;
                }
                return false;
            });
        }
    },
    /**
     * Binds a method to a given context.
     * @param fn (function)
     * @param context (Object) this-context to preserve for invocations
     * @static
     */
    bind: function(fn, context) {
        return function() {
            return fn.apply(context, arguments);
        };
    },
    /**
     * @static
     * Binds an event handler that is called only once, then automatically unbound.
     * @param eventName (String)
     * @param handler (function) callback with any number of arguments it expects from the notifier
     */
    listenOnce: function(eventName, handler) {
        this.assertString(eventName, 'Invalid event name for util.listenOnce()');
        this.assertFunction(handler, 'Invalid handler function for util.listenOnce()');
        let self = this,
            wrappedHandler = function() {
                self.unlisten(eventName, wrappedHandler);
                handler.apply(self, arguments);
                // console.log('Finished call of listenOnce-wrapped handler.');
            };
        this.listen(eventName, wrappedHandler);
        // console.log('Bound once-only-handler for event "%s"', eventName);
    },

    /**
     * @static
     * Calls all listeners that were registered via {@link #listen()} for the given event name,
     * passing to each handler the additional arguments.
     * @param eventName (String)
     * @params <any> (mixed) additional arguments to be passed as parameters to the registered listeners
     */
    notify: function(eventName /* , params... */) {
        this.assertString(eventName, 'Invalid event name for util.notify()');
        // console.info('Event "%s" triggered', eventName);
        let handlerArgs =  _.toArray(arguments).splice(1);
        _.each(listeners[eventName] || [], function(handler) {
            handler.apply(null, handlerArgs);
        });
    },

    /**
     * Rounds the given number to a given number of decimals (mathematically correct, unlike Math.round)
     * See https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Math/round)
     * @param {number} value
     * @param {number} [decimals=0]
     * @returns {number}
     */
    round10: function(value, decimals) {
        return decimalAdjust('round', value, -(decimals||0));
    },
    /**
     * @param {number} value
     * @param {number} [decimals=0]
     * @returns {number}
     */
    floor10: function(value, decimals) {
        return decimalAdjust('floor', value, -(decimals||0));
    },
    /**
     * @param {number} value
     * @param {number} [decimals=0]
     * @returns {number}
     */
    ceil10: function(value, decimals) {
        return decimalAdjust('ceil', value, -(decimals||0));
    }
};

export default util;